import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Check, Home } from 'lucide-react';
import { useEnabledBuildingTypesSettings } from '../../hooks/superadmin/useEnabledBuildingTypesSettings';
import { ALL_BUILDING_TYPES, BuildingType } from '../../types/BuildingTypeEnums';
import { translate } from '../../types/EnumTranslations';

const sameTypes = (a: readonly BuildingType[], b: readonly BuildingType[]): boolean =>
    a.length === b.length && a.every(type => b.includes(type));

/** The building types this instance offers (`global/enabledBuildingTypes`). */
const EnabledBuildingTypesCard: React.FC = () => {
    const { loading, saved, initial, save } = useEnabledBuildingTypesSettings();
    const [selected, setSelected] = useState<BuildingType[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => { setSelected(initial); }, [initial]);

    const toggle = (type: BuildingType) => {
        setSelected(previous => previous.includes(type)
            ? previous.filter(t => t !== type)
            : [...previous, type]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);
        setSubmitting(true);
        try {
            await save(selected);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte spara byggnadstyperna');
        } finally {
            setSubmitting(false);
        }
    };

    const unchanged = saved !== null && sameTypes(selected, saved);

    return (
        <Card className="mb-4">
            <Card.Header className="d-flex align-items-center gap-2">
                <Home size={18} />
                Byggnadstyper
            </Card.Header>
            <Card.Body className="p-4">
                <p className="text-muted mb-3">
                    Styr vilka byggnadstyper som går att välja när en byggnad skapas eller
                    byter typ. Befintliga byggnader påverkas inte, och filter visar en
                    avstängd typ så länge någon byggnad har den. Apparna läser värdet vid
                    start, så en ändring slår igenom efter omstart.
                </p>

                {loading ? (
                    <Spinner animation="border" size="sm" />
                ) : (
                    <>
                        {saved === null && (
                            <Alert variant="warning">
                                Inga byggnadstyper är sparade för instansen, så inga byggnader kan
                                skapas. Valen nedan är förifyllda från den äldre inställningen.
                            </Alert>
                        )}

                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert variant="success" className="d-flex align-items-center gap-2">
                                <Check size={18} />
                                Inställningen har sparats
                            </Alert>
                        )}

                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3">
                                {ALL_BUILDING_TYPES.map(type => {
                                    const isOn = selected.includes(type);
                                    const isLast = isOn && selected.length === 1;
                                    return (
                                        <Form.Check
                                            key={type}
                                            type="switch"
                                            id={`enabled-building-type-${type}`}
                                            label={translate(type)}
                                            checked={isOn}
                                            onChange={() => toggle(type)}
                                            disabled={submitting || isLast}
                                            title={isLast ? 'Minst en byggnadstyp måste vara aktiverad' : undefined}
                                        />
                                    );
                                })}
                            </Form.Group>

                            <Button
                                type="submit"
                                variant="primary"
                                disabled={submitting || selected.length === 0 || unchanged}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Sparar...
                                    </>
                                ) : (
                                    'Spara'
                                )}
                            </Button>
                        </Form>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default EnabledBuildingTypesCard;
